import { useEffect, useRef, useState } from 'react'
import { useAppStore, useCmsStore, useFlavours } from '@/components/AppHooks';
import { useRouter } from 'next/navigation';
import { Loading } from '@/components/shared/Loading';
import { TopBack } from '@/components/shared/TopBack';
import { IconInfo, IconClock } from '@/components/icon/master';
import { CocktailList } from '@/components/shared/CocktailList';
import { Popup } from '@/components/shared/Popup';
import { ReplaceRichTextLink, transformDate } from '@/shared/utils';
import { TResultsProps } from '../type';
import { styleName } from '@/config/const';
import { getRecommendation } from '../api';
import { isCurrentTimeInDailyRangeIgnoreTimezone } from '@/shared/utils';
import { SimnoffTopMarquee } from '@/components/shared/Smirnoff/TopMarquee';
import { tracking } from '@/shared/tracking';

export function ResultsMaster(props: TResultsProps) {
  const appStore = useAppStore();
  const { answer, results, isReady } = appStore;
  const { locale, storeConfig } = useCmsStore()!
  const { storeStyleName, nonAlcoholStore, startTime, endTime, isReady: storeIsReady } = storeConfig
  const hasNonAlcoholStore = nonAlcoholStore?.id && nonAlcoholStore.cocktailList?.length > 0
  const flavours = useFlavours()
  const isSmirnoffKenya = storeStyleName === styleName.SmirnoffKenya
  const router = useRouter()
  const [_description, setDescription] = useState('')
  const quizComplete = useRef(false)
  useEffect(() => {
    if (results && !quizComplete.current) {
      quizComplete.current = true
      tracking({
        "event": "custom_event",
        "event_name": "quiz_complete",
        "step": "success"
      })
    }
  }, [results])
  const { tips: _tips } = props
  useEffect(() => {
    if (isReady === undefined || !storeIsReady) return
    if (!answer.atmosphere) {
      return router.replace(`/${locale}/quiz${location.search}`);
    }
    let replacedText = ReplaceRichTextLink(_tips)
    replacedText = replacedText?.replace('[#vibesName#]', answer.atmosphereName)
    setDescription(replacedText)
    if (!results?.cocktailRecommendations) {
      getRecommendation({
        appStore,
        storeConfig
      })
    }
  }, [_tips, isReady, storeIsReady])

  const [selectedFlavor, setSelectedFlavor] = useState('');
  const [tips, setTips] = useState<{
    show: boolean,
    content?: string,
    flavor?: string,
    index?: number,
    left?: number,
  }>({
    show: false,
    flavor: '',
    index: -1,
    left: 0,
  });
  const showReminderTip = storeStyleName === styleName.Master && hasNonAlcoholStore && startTime && endTime && isCurrentTimeInDailyRangeIgnoreTimezone(startTime, endTime)
  return (<>
    <Loading show={(!results && props.loading !== null)} loading={props.loading[0]} />
    {/* <Loading show={true} loading={props.loading[0]}></Loading> */}
    <div className={`${isSmirnoffKenya ? 'page-smirnoff-results' : 'page-results page-container'}`}>
      {
        isSmirnoffKenya && <SimnoffTopMarquee />
      }
      <div className='smirnoff-page-container'>
        <div className="top-wrapper">
          <div className='left-contents'>
            <TopBack onClick={() => {
              router.replace(`/${locale}/home${location.search}`)
            }} />
            <h1 className="title">{props.title}</h1>
            <div className='description' onClick={(e) => {
              if ((e.target as any).nodeName === 'A') {
                tracking({
                  "event": "in_page_button",
                  "click_text": "Start Again",
                  "destination_page_path": location.pathname + location.search,
                  "link_url": location.href,
                  "detail": "Start Again"
                })
              }

            }} dangerouslySetInnerHTML={{ __html: _description }}></div>
          </div>

          <div className='right-contents'>
            <div className='result-subtitle'>{props.profileTitle}</div>
            <div className='flavors-container'>
              <div className='top-flavors'>
                {results?.consumerTopFlavors.slice(0, 3).map((item, index) => (
                  <div key={item} className={`flavor-tag group ${selectedFlavor === item ? 'selected' : ''}`} onClick={() => {
                    if (innerWidth < 1024) {
                      setSelectedFlavor(item)
                    }
                  }} onMouseEnter={(e) => {
                    if (innerWidth >= 1024) {
                      let target = e.target as HTMLElement
                      if (!target.classList.contains('flavor-tag')) {
                        target = target.closest('.flavor-tag') as HTMLElement
                      }
                      const parent = target.parentElement as HTMLElement
                      const { left: parentLeft } = parent.getBoundingClientRect()
                      const { width, left } = target.getBoundingClientRect()
                      setTips({
                        ...tips,
                        show: true,
                        flavor: item,
                        index: index,
                        left: (left - parentLeft) + width / 2,
                      })
                    }

                  }} onMouseLeave={() => {
                    if (innerWidth >= 1024) {
                      setTips({
                        ...tips,
                        show: false,
                      })
                    }
                  }} >
                    <span className='flavor-text'>{
                      flavours.find((fitem) => fitem.flavourID === item)?.flavourName ?? item
                    }</span>
                    <IconInfo className='icon-info' />
                  </div>
                ))}

              </div>
              <div className={`flavor-tips-wrapper tips-${tips.index} ${tips.show ? 'show' : 'hide'}`} style={{
                '--left': `${tips.left}px`
              } as React.CSSProperties}>
                {
                  flavours.find((item) => item.flavourName === tips.flavor || item.flavourID === tips.flavor)?.flavourDescription
                }
              </div>
            </div>

          </div>

        </div>
        <div className='results-wrapper'>
          {
            showReminderTip && <div className={`times-reminder`}>
              <IconClock className='icon-clock' />
              <div className='times-reminder-content'>
                {props.messageNonAlcoholicTime.replace('[#startTime#]', transformDate(startTime)).replace('[#endTime#]', transformDate(endTime))}
              </div>
            </div>
          }

          <CocktailList className='result-cocktail-list' cocktailList={results?.cocktailRecommendations} cta={props.discoverCTA} />
        </div>
      </div>

    </div>
    <Popup className='popup-result-flavor' show={selectedFlavor !== ''} onClose={() => {
      setSelectedFlavor('')
    }} headSlot={<div className='result-popup-header'>{
      flavours.find((fitem) => fitem.flavourID === selectedFlavor)?.flavourName ?? selectedFlavor
    }</div>}>
      <div className='popup-flavor-content'>
        {
          flavours.find((item) => item.flavourName === selectedFlavor || item.flavourID === selectedFlavor)?.flavourDescription
        }
      </div>
    </Popup>
  </>
  )
}
